import { MailEncryptionPage } from './pages/mail-encryption';
import { InboxRulesPage } from './pages/inbox-rules';
import { useI18n } from '@platform/i18n';
import { breadcrumbFor, EmptyState, findNavItem, localizeRegistry, LoginScreen, PlaceholderPage, PlatformProvider, useErrorText } from '@platform/ui';
import { MailIcon } from 'lucide-react';
import { useEffect, useState, type ComponentType } from 'react';
import type { MfaPendingInfo } from '@platform/api-client';
import { MfaChallenge, MfaSecurityBody } from './mfa';
import { PageBody, PageHeader } from '@platform/ui';
import { SettingsTabs } from './pages/settings-tabs';
import { CalendarSettingsPage } from './pages/settings-calendar';
import { createBrowserRouter, Navigate, RouterProvider, useLocation, useNavigate, type RouteObject } from 'react-router';
import { api, USE_MOCK } from './api';
import { AuthProvider, useAuth } from './auth';
import { WebmailLayout } from './layout';
import { webmailNavigation } from './navigation';
import { CalendarPage } from './pages/calendar';
import { ContactsPage } from './pages/contacts';
import { TasksPage } from './pages/tasks';
import { MailPage } from './pages/mail';
import { AutomaticRepliesPage } from './pages/automatic-replies';
import { QuarantinePage } from './pages/quarantine';
import { PublicFoldersPage } from './pages/public-folders';
import { SettingsPage } from './pages/settings';
import { RegionalSettingsPage } from './regional-setup';
import { MyListsPage } from './pages/my-lists';

function LoginRoute() {
  const { t } = useI18n();
  const { session, login, admissionError, retryAdmission } = useAuth();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [pending, setPending] = useState<MfaPendingInfo>();
  const [attempt, setAttempt] = useState(0);
  useEffect(() => { let active = true; void api.mfa?.pending().then(value => { if (active) setPending(value); }, () => { if (active) setPending(undefined); }); return () => { active = false; }; }, [attempt, admissionError]);
  if (session && !location.search.includes('federation=unlock')) return <Navigate to={from} replace />;
  return (
    <LoginScreen
      identity={api.identity}
      onFederationDone={() => { retryAdmission(); void navigate(from, { replace: true }); }}
      onFederationMfa={() => setAttempt(value => value + 1)}
      subtitle={t('auth.webmailSubtitle')}
      logo={<MailIcon />}
      hint={USE_MOCK ? t('auth.previewHint') : undefined}
      describeError={errorText.login}
      admissionError={admissionError ? errorText.login(admissionError) : undefined}
      onRetryAdmission={admissionError ? retryAdmission : undefined}
      onLogin={async (credentials) => {
        try { await login(credentials); }
        catch (error: unknown) { if (typeof error === 'object' && error && 'code' in error && error.code === 'mfa_required') { setAttempt(value => value + 1); return; } throw error; }
        await navigate(from, { replace: true });
      }}
    >{pending ? <MfaChallenge pending={pending} onDone={() => { setPending(undefined); retryAdmission(); }} onCancel={() => setPending(undefined)} /> : undefined}</LoginScreen>
  );
}

function SecuritySettings() {
  const { t } = useI18n();
  return <div className="min-h-0 flex-1 overflow-y-auto"><PageHeader title={t('mfa.security')} tabs={<SettingsTabs active="security" />} /><PageBody width="form"><MfaSecurityBody /></PageBody></div>;
}

function PlannedRoute() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigation = localizeRegistry(webmailNavigation, t);
  return <PlaceholderPage title={findNavItem(navigation, pathname)?.item.label ?? ''} breadcrumb={breadcrumbFor(navigation, pathname)} />;
}

function MissingRoute() {
  const { t } = useI18n();
  return <EmptyState title={t('errorPage.notFoundTitle')} description={t('errorPage.notFoundDescription')} />;
}

/** Built pages by navigation id; other registry items render the planned placeholder. */
const pages: Partial<Record<string, ComponentType>> = { mail: MailPage, calendar: CalendarPage, contacts: ContactsPage, tasks: TasksPage, settings: SettingsPage, quarantine: QuarantinePage, publicFolders: PublicFoldersPage };

const pageRoutes: RouteObject[] = webmailNavigation.flatMap((group) =>
  group.items.map((item) => {
    const Page = pages[item.id] ?? PlannedRoute;
    return item.path === '/' ? { index: true, element: <Page /> } : { path: item.path.slice(1), element: <Page /> };
  }),
);

const router = createBrowserRouter(
  [
    { path: '/login', element: <LoginRoute /> },
    { path: '/', element: <WebmailLayout />, children: [...pageRoutes, { path: 'settings/security', element: <SecuritySettings /> }, { path: 'settings/mail-encryption', element: <MailEncryptionPage /> }, { path: 'settings/regional', element: <RegionalSettingsPage /> }, { path: 'settings/calendar', element: <CalendarSettingsPage /> }, { path: 'settings/inbox-rules', element: <InboxRulesPage /> }, { path: 'settings/automatic-replies', element: <AutomaticRepliesPage /> }, { path: 'settings/my-lists', element: <MyListsPage /> }, { path: 'settings/personal-lists', element: <Navigate to="/settings/my-lists" replace /> }, { path: 'settings/typed-lists', element: <Navigate to="/settings/my-lists" replace /> }, { path: '*', element: <MissingRoute /> }] },
  ],
  { basename: import.meta.env.BASE_URL.replace(/\/$/, '') || '/' },
);

export function App() {
  return (
    <PlatformProvider appNameKey="webmail.appName" runtime={api.runtime}>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </PlatformProvider>
  );
}
